import { supabase } from '../lib/supabase'
import { comercialContatosPortalService } from './comercialContatosPortal'
import type {
  CreateFormInput,
  CreateFormVersionInput,
  Form,
  FormAssignment,
  FormField,
  FormResponse,
  FormResponseRelatorioFiltros,
  FormResponseRelatorioResult,
  FormResponseRelatorioRow,
  SaveFormResponseInput,
  UpdateFormMetaInput,
} from '../types/forms'

const CHART_MAX_ROWS = 3000

type FormEmbed = {
  id: string
  name: string
  version: number
  schema: FormField[] | null
  form_group_id: string
}

type FormResponseQueryRow = {
  id: string
  form_id: string
  form_version: number
  broker_id: string
  attendant_id: string | null
  broker_contact_log_id: string | null
  responses: Record<string, unknown> | null
  status: FormResponse['status']
  created_at: string
  forms: FormEmbed | FormEmbed[] | null
}

function firstEmbed<T>(value: T | T[] | null | undefined): T | null {
  if (!value) return null
  return Array.isArray(value) ? value[0] ?? null : value
}

function addOneDayYmd(ymd: string): string {
  const [year, month, day] = ymd.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day + 1))
  return date.toISOString().slice(0, 10)
}

function applyResponseDateFilter<
  T extends { gte: (col: string, val: string) => T; lt: (col: string, val: string) => T },
>(query: T, filtros: FormResponseRelatorioFiltros): T {
  let next = query
  if (filtros.data_inicio) {
    next = next.gte('created_at', `${filtros.data_inicio}T00:00:00-03:00`)
  }
  if (filtros.data_fim) {
    next = next.lt('created_at', `${addOneDayYmd(filtros.data_fim)}T00:00:00-03:00`)
  }
  return next
}

async function listFormIdsByGroup(formGroupId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('forms')
    .select('id')
    .eq('form_group_id', formGroupId)

  if (error) throw error
  return (data ?? []).map((row) => row.id as string)
}

async function fetchBrokerLookup(brokerIds: string[]): Promise<Map<string, { name: string; id_scgp: number | null }>> {
  const map = new Map<string, { name: string; id_scgp: number | null }>()
  const unique = Array.from(new Set(brokerIds.filter(Boolean)))
  if (unique.length === 0) return map

  const { data, error } = await supabase
    .from('brokers')
    .select('id, name, id_scgp')
    .in('id', unique)

  if (error) throw error

  for (const row of data ?? []) {
    map.set(row.id, {
      name: typeof row.name === 'string' && row.name.trim() ? row.name.trim() : '—',
      id_scgp: typeof row.id_scgp === 'number' ? row.id_scgp : null,
    })
  }

  return map
}

function mapRelatorioRow(
  row: FormResponseQueryRow,
  brokers: Map<string, { name: string; id_scgp: number | null }>
): FormResponseRelatorioRow | null {
  const form = firstEmbed(row.forms)
  if (!form) return null
  const broker = brokers.get(row.broker_id)

  return {
    id: row.id,
    form_id: row.form_id,
    form_group_id: form.form_group_id,
    form_name: form.name,
    form_version: row.form_version,
    schema: normalizeSchema(form.schema),
    broker_id: row.broker_id,
    broker_name: broker?.name ?? '—',
    broker_id_scgp: broker?.id_scgp ?? null,
    attendant_id: row.attendant_id,
    broker_contact_log_id: row.broker_contact_log_id,
    responses: row.responses ?? {},
    status: row.status,
    created_at: row.created_at,
  }
}

const RESPONSE_RELATORIO_SELECT =
  'id, form_id, form_version, broker_id, attendant_id, broker_contact_log_id, responses, status, created_at, forms:forms!form_responses_form_id_fkey(id, name, version, schema, form_group_id)'

type FormRow = Omit<Form, 'categories' | 'schema'> & {
  schema: FormField[] | null
}

function normalizeSchema(schema: FormField[] | null | undefined): FormField[] {
  return Array.isArray(schema) ? schema : []
}

function mapForm(row: FormRow, categories: string[] = []): Form {
  return {
    ...row,
    schema: normalizeSchema(row.schema),
    categories,
  }
}

async function fetchAssignmentsByGroupIds(
  formGroupIds: string[]
): Promise<Map<string, string[]>> {
  const map = new Map<string, string[]>()
  if (formGroupIds.length === 0) return map

  const { data, error } = await supabase
    .from('form_assignments')
    .select('form_group_id, category')
    .in('form_group_id', formGroupIds)

  if (error) throw error

  for (const row of data ?? []) {
    const list = map.get(row.form_group_id) ?? []
    list.push(row.category)
    map.set(row.form_group_id, list)
  }

  for (const [key, list] of map) {
    map.set(
      key,
      Array.from(new Set(list)).sort((a, b) => a.localeCompare(b, 'pt-BR'))
    )
  }

  return map
}

async function replaceAssignments(
  formGroupId: string,
  categories: string[]
): Promise<void> {
  const normalized = Array.from(
    new Set(categories.map((c) => c.trim()).filter(Boolean))
  )

  const { error: deleteError } = await supabase
    .from('form_assignments')
    .delete()
    .eq('form_group_id', formGroupId)

  if (deleteError) throw deleteError

  if (normalized.length === 0) return

  const { error: insertError } = await supabase.from('form_assignments').insert(
    normalized.map((category) => ({
      form_group_id: formGroupId,
      category,
    }))
  )

  if (insertError) throw insertError
}

export const formsService = {
  async listFormGroups(): Promise<Form[]> {
    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .is('deleted_at', null)
      .order('version', { ascending: false })

    if (error) throw error

    const rows = (data ?? []) as FormRow[]
    const seen = new Set<string>()
    const latest: FormRow[] = []

    for (const row of rows) {
      if (seen.has(row.form_group_id)) continue
      seen.add(row.form_group_id)
      latest.push(row)
    }

    const categoriesMap = await fetchAssignmentsByGroupIds(
      latest.map((r) => r.form_group_id)
    )

    return latest
      .map((row) => mapForm(row, categoriesMap.get(row.form_group_id) ?? []))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  },

  async listVersions(formGroupId: string): Promise<Form[]> {
    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('form_group_id', formGroupId)
      .is('deleted_at', null)
      .order('version', { ascending: false })

    if (error) throw error

    const categoriesMap = await fetchAssignmentsByGroupIds([formGroupId])
    const categories = categoriesMap.get(formGroupId) ?? []

    return ((data ?? []) as FormRow[]).map((row) => mapForm(row, categories))
  },

  async getFormWithCategories(formGroupId: string): Promise<Form | null> {
    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('form_group_id', formGroupId)
      .is('deleted_at', null)
      .eq('is_active', true)
      .order('version', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) throw error
    if (!data) {
      const { data: fallback, error: fallbackError } = await supabase
        .from('forms')
        .select('*')
        .eq('form_group_id', formGroupId)
        .is('deleted_at', null)
        .order('version', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (fallbackError) throw fallbackError
      if (!fallback) return null

      const categoriesMap = await fetchAssignmentsByGroupIds([formGroupId])
      return mapForm(fallback as FormRow, categoriesMap.get(formGroupId) ?? [])
    }

    const categoriesMap = await fetchAssignmentsByGroupIds([formGroupId])
    return mapForm(data as FormRow, categoriesMap.get(formGroupId) ?? [])
  },

  async createForm(input: CreateFormInput): Promise<Form> {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('forms')
      .insert({
        name: input.name.trim(),
        version: 1,
        schema: input.schema,
        is_active: input.is_active ?? true,
        created_by: user?.id ?? null,
      })
      .select('*')
      .single()

    if (error) throw error

    const form = data as FormRow
    await replaceAssignments(form.form_group_id, input.categories)

    return mapForm(form, [...input.categories].sort((a, b) => a.localeCompare(b, 'pt-BR')))
  },

  async createNewVersion(
    formGroupId: string,
    input: CreateFormVersionInput
  ): Promise<Form> {
    const { data: versions, error: versionsError } = await supabase
      .from('forms')
      .select('version')
      .eq('form_group_id', formGroupId)
      .is('deleted_at', null)
      .order('version', { ascending: false })
      .limit(1)

    if (versionsError) throw versionsError

    const maxVersion = versions?.[0]?.version ?? 0
    const nextVersion = maxVersion + 1

    const {
      data: { user },
    } = await supabase.auth.getUser()

    const { error: deactivateError } = await supabase
      .from('forms')
      .update({ is_active: false })
      .eq('form_group_id', formGroupId)
      .is('deleted_at', null)

    if (deactivateError) throw deactivateError

    const { data, error } = await supabase
      .from('forms')
      .insert({
        form_group_id: formGroupId,
        name: input.name.trim(),
        version: nextVersion,
        schema: input.schema,
        is_active: true,
        created_by: user?.id ?? null,
      })
      .select('*')
      .single()

    if (error) throw error

    if (input.categories) {
      await replaceAssignments(formGroupId, input.categories)
    }

    const categoriesMap = await fetchAssignmentsByGroupIds([formGroupId])
    return mapForm(data as FormRow, categoriesMap.get(formGroupId) ?? [])
  },

  async updateFormMeta(id: string, input: UpdateFormMetaInput): Promise<Form> {
    const { data: current, error: currentError } = await supabase
      .from('forms')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .single()

    if (currentError) throw currentError

    const formGroupId = (current as FormRow).form_group_id

    if (input.name != null) {
      const { error: nameError } = await supabase
        .from('forms')
        .update({ name: input.name.trim() })
        .eq('form_group_id', formGroupId)
        .is('deleted_at', null)

      if (nameError) throw nameError
    }

    if (input.is_active != null) {
      if (input.is_active) {
        const { error: deactivateError } = await supabase
          .from('forms')
          .update({ is_active: false })
          .eq('form_group_id', formGroupId)
          .is('deleted_at', null)

        if (deactivateError) throw deactivateError

        const { error: activateError } = await supabase
          .from('forms')
          .update({ is_active: true })
          .eq('id', id)

        if (activateError) throw activateError
      } else {
        const { error: deactivateError } = await supabase
          .from('forms')
          .update({ is_active: false })
          .eq('id', id)

        if (deactivateError) throw deactivateError
      }
    }

    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error

    const categoriesMap = await fetchAssignmentsByGroupIds([formGroupId])
    return mapForm(data as FormRow, categoriesMap.get(formGroupId) ?? [])
  },

  async updateAssignments(
    formGroupId: string,
    categories: string[]
  ): Promise<void> {
    await replaceAssignments(formGroupId, categories)
  },

  async deleteFormGroup(formGroupId: string): Promise<void> {
    const { error } = await supabase
      .from('forms')
      .update({ deleted_at: new Date().toISOString(), is_active: false })
      .eq('form_group_id', formGroupId)
      .is('deleted_at', null)

    if (error) throw error
  },

  async getFormForBroker(classification: string | null | undefined): Promise<Form | null> {
    const category = classification?.trim()
    if (!category) return null

    const { data: assignment, error: assignmentError } = await supabase
      .from('form_assignments')
      .select('form_group_id')
      .eq('category', category)
      .limit(1)
      .maybeSingle()

    if (assignmentError) throw assignmentError
    if (!assignment) return null

    const { data, error } = await supabase
      .from('forms')
      .select('*')
      .eq('form_group_id', assignment.form_group_id)
      .eq('is_active', true)
      .is('deleted_at', null)
      .order('version', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) throw error
    if (!data) return null

    return mapForm(data as FormRow, [category])
  },

  async listAssignments(formGroupId: string): Promise<FormAssignment[]> {
    const { data, error } = await supabase
      .from('form_assignments')
      .select('*')
      .eq('form_group_id', formGroupId)
      .order('category', { ascending: true })

    if (error) throw error
    return (data ?? []) as FormAssignment[]
  },

  async saveFormResponse(payload: SaveFormResponseInput): Promise<FormResponse> {
    const { data, error } = await supabase
      .from('form_responses')
      .insert({
        form_id: payload.form_id,
        form_version: payload.form_version,
        broker_id: payload.broker_id,
        attendant_id: payload.attendant_id ?? null,
        broker_contact_log_id: payload.broker_contact_log_id ?? null,
        responses: payload.responses,
        status: payload.status ?? 'draft',
      })
      .select('*')
      .single()

    if (error) throw error
    return data as FormResponse
  },

  async getResponsesForBroker(brokerId: string): Promise<FormResponse[]> {
    const { data, error } = await supabase
      .from('form_responses')
      .select('*')
      .eq('broker_id', brokerId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data ?? []) as FormResponse[]
  },

  async listFormGroupsForRelatorio(): Promise<Form[]> {
    return this.listFormGroups()
  },

  async listResponsesForRelatorio(
    filtros: FormResponseRelatorioFiltros,
    page = 1,
    pageSize = 20
  ): Promise<FormResponseRelatorioResult> {
    const safePage = Math.max(1, page)
    const safePageSize = Math.max(1, pageSize)
    const from = (safePage - 1) * safePageSize
    const to = from + safePageSize - 1

    const formIds = await listFormIdsByGroup(filtros.form_group_id)
    if (formIds.length === 0) {
      return { data: [], total: 0, page: safePage, pageSize: safePageSize }
    }

    const resolved = await comercialContatosPortalService.resolveBrokerIdsForComercialFiltro({
      broker_id: filtros.broker_id,
      comercial_user_id: filtros.comercial_user_id,
      consultant_user_id: filtros.consultant_user_id,
    })
    if (resolved.empty) {
      return {
        data: [],
        total: 0,
        page: safePage,
        pageSize: safePageSize,
        empty_reason: resolved.empty_reason ?? 'comercial_sem_corretoras',
      }
    }

    let query = supabase
      .from('form_responses')
      .select(RESPONSE_RELATORIO_SELECT, { count: 'exact' })
      .in('form_id', formIds)
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(from, to)

    query = applyResponseDateFilter(query, filtros)
    if (resolved.broker_ids?.length === 1) {
      query = query.eq('broker_id', resolved.broker_ids[0])
    } else if (resolved.broker_ids && resolved.broker_ids.length > 1) {
      query = query.in('broker_id', resolved.broker_ids)
    }

    const { data, error, count } = await query
    if (error) throw error

    const raw = (data ?? []) as FormResponseQueryRow[]
    const brokers = await fetchBrokerLookup(raw.map((row) => row.broker_id))
    const rows = raw
      .map((row) => mapRelatorioRow(row, brokers))
      .filter((row): row is FormResponseRelatorioRow => row != null)

    return {
      data: rows,
      total: count ?? 0,
      page: safePage,
      pageSize: safePageSize,
    }
  },

  async listResponsesForChart(
    filtros: FormResponseRelatorioFiltros
  ): Promise<FormResponseRelatorioRow[]> {
    const formIds = await listFormIdsByGroup(filtros.form_group_id)
    if (formIds.length === 0) return []

    const resolved = await comercialContatosPortalService.resolveBrokerIdsForComercialFiltro({
      broker_id: filtros.broker_id,
      comercial_user_id: filtros.comercial_user_id,
      consultant_user_id: filtros.consultant_user_id,
    })
    if (resolved.empty) return []

    let query = supabase
      .from('form_responses')
      .select(RESPONSE_RELATORIO_SELECT)
      .in('form_id', formIds)
      .order('created_at', { ascending: false })
      .limit(CHART_MAX_ROWS)

    query = applyResponseDateFilter(query, filtros)
    if (resolved.broker_ids?.length === 1) {
      query = query.eq('broker_id', resolved.broker_ids[0])
    } else if (resolved.broker_ids && resolved.broker_ids.length > 1) {
      query = query.in('broker_id', resolved.broker_ids)
    }

    const { data, error } = await query
    if (error) throw error

    const raw = (data ?? []) as FormResponseQueryRow[]
    const brokers = await fetchBrokerLookup(raw.map((row) => row.broker_id))
    return raw
      .map((row) => mapRelatorioRow(row, brokers))
      .filter((row): row is FormResponseRelatorioRow => row != null)
  },
}
